import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useBateaux, Bateau } from "@/hooks/useBateaux";
import { useMaintenances } from "@/hooks/useMaintenances";
import { Bot, Anchor, Calendar, Settings, Sailboat, Shield, Zap, Droplets, Clock, Edit2, Save, X, Euro } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SelecteurNavire from "@/components/bateaux/SelecteurNavire";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ArticlesCritiquesCard from "@/components/dashboard/ArticlesCritiquesCard";
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useDecryptedBoatFields } from "@/hooks/useDecryptedBoatFields";
import { useFindPortByName } from "@/hooks/usePorts";
import { Anchor as AnchorIcon } from "lucide-react";

const categoryIcons = {
  'Moteur': Settings,
  'Coque': Anchor,
  'Voiles': Sailboat,
  'Équipements de sécurité': Shield,
  'Électricité': Zap,
  'Plomberie': Droplets,
};

const getStatusColor = (statut: string) => {
  switch (statut) {
    case 'En attente': return 'bg-green-100 text-green-800';
    case 'En cours': return 'bg-yellow-100 text-yellow-800';
    case 'Terminée': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const DashboardPage = () => {
  const { bateaux, loading, loadSucceeded, getBateauParDefaut, modifierBateau } = useBateaux();
  const { maintenances, loading: maintenancesLoading } = useMaintenances();
  const navigate = useNavigate();
  const [selectedBateauId, setSelectedBateauId] = useState<string | null>(null);
  const [editingEngineHours, setEditingEngineHours] = useState(false);
  const [engineHours, setEngineHours] = useState('');
  const [selectedCostYear, setSelectedCostYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    // Rediriger vers l'ajout d'un bateau seulement si on a confirmé qu'il n'y en a aucun
    if (!loading && loadSucceeded && bateaux.length === 0) {
      navigate('/mes-bateaux');
      return;
    }

    // Utiliser le navire par défaut ou le premier disponible
    if (!loading && bateaux.length > 0 && !selectedBateauId) {
      const bateauParDefaut = getBateauParDefaut();
      if (bateauParDefaut) {
        setSelectedBateauId(bateauParDefaut.id);
        setEngineHours(bateauParDefaut.heures_moteur?.toString() || '0');
      }
    }
  }, [bateaux, loading, navigate, selectedBateauId, getBateauParDefaut]);

  // Synchroniser les heures moteur quand le bateau sélectionné change
  useEffect(() => {
    if (selectedBateauId) {
      const selectedBoat = bateaux.find(b => b.id === selectedBateauId);
      if (selectedBoat) {
        setEngineHours(selectedBoat.heures_moteur?.toString() || '0');
      }
    }
  }, [selectedBateauId, bateaux]);

  const handleBateauChange = (bateauId: string) => {
    console.log('Changement de bateau vers:', bateauId);
    setSelectedBateauId(bateauId);
    setEditingEngineHours(false); // Fermer l'édition si ouverte
  };

  const handleSaveEngineHours = async () => {
    if (!selectedBateauId) return;
    
    const hours = parseInt(engineHours) || 0;
    console.log('Saving engine hours:', hours, 'for boat:', selectedBateauId);
    
    const result = await modifierBateau(selectedBateauId, { heures_moteur: hours });
    if (result && !result.error) {
      setEditingEngineHours(false);
      console.log('Engine hours updated successfully');
    }
  };

  const handleCancelEditEngineHours = () => {
    const selectedBoat = bateaux.find(b => b.id === selectedBateauId);
    setEngineHours(selectedBoat?.heures_moteur?.toString() || '0');
    setEditingEngineHours(false);
  };

  const selectedBoat = bateaux.find(b => b.id === selectedBateauId) || getBateauParDefaut();

  const { decrypted } = useDecryptedBoatFields(selectedBoat?.id, {
    immatriculation: selectedBoat?.immatriculation,
    mmsi: selectedBoat?.mmsi,
    numero_hin: selectedBoat?.numero_hin,
  });

  const { port: portAttache } = useFindPortByName(selectedBoat?.port_attache);

  if (loading || maintenancesLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="text-center p-6 bg-white rounded-lg shadow-sm">
          <Anchor className="mx-auto h-12 w-12 text-nautic animate-pulse" />
          <h3 className="mt-2 text-xl font-semibold text-nautic">Chargement...</h3>
          <p className="mt-1 text-gray-500">
            Récupération de vos données...
          </p>
        </div>
      </div>
    );
  }

  if (!selectedBoat) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="text-center p-6 bg-white rounded-lg shadow-sm">
          <Anchor className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-xl font-semibold text-gray-900">Aucun Navire</h3>
          <p className="mt-1 text-gray-500">
            Ajoutez votre premier navire pour commencer.
          </p>
          <Button 
            onClick={() => navigate('/mes-bateaux')}
            className="mt-4 bg-nautic-gold hover:bg-nautic-lightgold text-nautic"
          >
            Ajouter un navire
          </Button>
        </div>
      </div>
    );
  }

  // Filtrer les maintenances pour le navire sélectionné
  const maintenancesNavire = maintenances.filter(m => m.bateau_id === selectedBoat.id);
  
  // Obtenir les 10 prochaines maintenances (pas terminées, triées par date d'échéance)
  const today = new Date().toISOString().split('T')[0];
  const currentYear = new Date().getFullYear();
  
  // Générer la liste des années disponibles (de l'année actuelle à 5 ans en arrière + 1 an en avant)
  const availableYears = Array.from({ length: 7 }, (_, i) => currentYear + 1 - i);
  
  // Calculer le coût total des maintenances de l'année sélectionnée (toutes les maintenances avec un prix)
  const coutMaintenancesAnnee = maintenancesNavire
    .filter(m => {
      const maintenanceYear = new Date(m.date_echeance).getFullYear();
      return maintenanceYear === selectedCostYear && m.prix;
    })
    .reduce((total, m) => total + (m.prix || 0), 0);
  
  // Nombre de maintenances comptabilisées
  const nombreMaintenancesAnnee = maintenancesNavire.filter(m => {
    const maintenanceYear = new Date(m.date_echeance).getFullYear();
    return maintenanceYear === selectedCostYear && m.prix;
  }).length;
  
  // Toutes les maintenances non terminées triées par date d'échéance
  const prochainesMaintenances = maintenancesNavire
    .filter(m => m.statut !== 'Terminée')
    .sort((a, b) => new Date(a.date_echeance).getTime() - new Date(b.date_echeance).getTime())
    .slice(0, 10);
  
  // Séparer les maintenances en retard pour l'affichage
  const maintenancesEnRetard = prochainesMaintenances.filter(m => m.date_echeance < today);

  return (
    <div className="space-y-4 lg:space-y-6 p-4 lg:p-0">
      <div>
        <h1 className="text-xl lg:text-2xl font-bold tracking-tight text-nautic">Tableau de bord</h1>
        <p className="text-sm lg:text-base text-muted-foreground">
          Aperçu de votre navire et activités récentes.
        </p>
      </div>

      {/* Sélecteur de navire */}
      <SelecteurNavire 
        bateaux={bateaux}
        selectedBateauId={selectedBateauId}
        onBateauChange={handleBateauChange}
      />


      {/* Fiche du bateau sélectionné */}
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 border">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl lg:text-2xl font-bold text-nautic">{selectedBoat.nom}</h2>
              {portAttache && (
                <Button
                  size="sm"
                  onClick={() => navigate(`/ports/${portAttache.id}`)}
                  className="bg-nautic-gold hover:bg-nautic-lightgold text-nautic border-0"
                >
                  <AnchorIcon className="h-4 w-4 mr-1" />
                  Port d'attache services
                </Button>
              )}
            </div>
            <p className="text-sm lg:text-base text-muted-foreground">{selectedBoat.nom_constructeur} {selectedBoat.nom_modele} ({selectedBoat.annee})</p>
            <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4 text-xs lg:text-sm text-gray-500 mt-1">
              {(selectedBoat.annee_construction || selectedBoat.annee_achat) && (
                <div>
                  {selectedBoat.annee_construction && (
                    <span>Construction: {selectedBoat.annee_construction}</span>
                  )}
                  {selectedBoat.annee_construction && selectedBoat.annee_achat && ' • '}
                  {selectedBoat.annee_achat && (
                    <span>Achat: {selectedBoat.annee_achat}</span>
                  )}
                </div>
              )}
              {/* Heures moteur éditables */}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {editingEngineHours ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={engineHours}
                      onChange={(e) => setEngineHours(e.target.value)}
                      className="w-20 h-6 text-sm"
                      min="0"
                      autoFocus
                    />
                    <span className="text-sm">h moteur</span>
                    <Button size="sm" variant="ghost" onClick={handleSaveEngineHours} className="h-6 w-6 p-0">
                      <Save className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleCancelEditEngineHours} className="h-6 w-6 p-0">
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{selectedBoat.heures_moteur || 0}h moteur</span>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setEditingEngineHours(true)}
                      className="h-6 w-6 p-0"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/mes-bateaux')}
            className="bg-nautic-gold hover:bg-nautic-lightgold text-nautic border-0 w-full lg:w-auto"
          >
            Gérer mes navires
          </Button>
        </div>

        {/* Photo du navire si disponible */}
        {selectedBoat.photo_url && (
          <div className="mb-4 lg:mb-6">
            <img 
              src={selectedBoat.photo_url} 
              alt={selectedBoat.nom} 
              className="w-full max-w-xs lg:max-w-md h-32 lg:h-48 object-cover rounded-lg mx-auto"
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          <div className="space-y-2">
            <h3 className="text-sm font-medium flex items-center gap-1">
              <span className="text-nautic-gold">⚓</span> Dimensions
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="min-w-0">
                <p className="text-xs lg:text-sm text-muted-foreground">Longueur</p>
                <p className="font-medium text-sm lg:text-base truncate">{selectedBoat.longueur} m</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs lg:text-sm text-muted-foreground">Largeur</p>
                <p className="font-medium text-sm lg:text-base truncate">{selectedBoat.largeur} m</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium flex items-center gap-1">
              <span className="text-nautic-gold">⚙️</span> Moteur
            </h3>
            <div className="space-y-1">
              <p className="text-xs lg:text-sm text-muted-foreground">Type</p>
              <p className="font-medium text-sm lg:text-base truncate">{selectedBoat.moteur}</p>
              <p className="text-xs lg:text-sm text-muted-foreground">Heures moteur</p>
              <p className="font-medium text-sm lg:text-base">{selectedBoat.heures_moteur || 0}h</p>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium flex items-center gap-1">
              <span className="text-nautic-gold">📊</span> Capacités
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="min-w-0">
                <p className="text-xs lg:text-sm text-muted-foreground">Carburant</p>
                <p className="font-medium text-sm lg:text-base truncate">{selectedBoat.capacite_carburant} L</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs lg:text-sm text-muted-foreground">Eau douce</p>
                <p className="font-medium text-sm lg:text-base truncate">{selectedBoat.capacite_eau} L</p>
              </div>
              {selectedBoat.capacite_eaux_usees && (
                <div className="col-span-2 min-w-0">
                  <p className="text-xs lg:text-sm text-muted-foreground">Eaux usées</p>
                  <p className="font-medium text-sm lg:text-base truncate">{selectedBoat.capacite_eaux_usees} L</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Informations supplémentaires */}
        {(selectedBoat.port_attache || selectedBoat.pavillon || decrypted.numero_hin || decrypted.mmsi || decrypted.immatriculation) && (
          <div className="mt-4 lg:mt-6 pt-4 lg:pt-6 border-t">
            <h3 className="text-sm font-medium text-muted-foreground mb-4">Informations complémentaires</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {decrypted.immatriculation && (
                <div className="min-w-0">
                  <p className="text-xs lg:text-sm text-muted-foreground">Immatriculation</p>
                  <p className="font-medium text-sm lg:text-base truncate">{decrypted.immatriculation}</p>
                </div>
              )}
              {selectedBoat.port_attache && (
                <div className="min-w-0">
                  <p className="text-xs lg:text-sm text-muted-foreground">Port d'attache</p>
                  <p className="font-medium text-sm lg:text-base truncate">{selectedBoat.port_attache}</p>
                </div>
              )}
              {selectedBoat.pavillon && (
                <div className="min-w-0">
                  <p className="text-xs lg:text-sm text-muted-foreground">Pavillon</p>
                  <p className="font-medium text-sm lg:text-base truncate">{selectedBoat.pavillon}</p>
                </div>
              )}
              {decrypted.numero_hin && (
                <div className="min-w-0">
                  <p className="text-xs lg:text-sm text-muted-foreground">Numéro HIN</p>
                  <p className="font-medium text-sm lg:text-base truncate">{decrypted.numero_hin}</p>
                </div>
              )}
              {decrypted.mmsi && (
                <div className="min-w-0">
                  <p className="text-xs lg:text-sm text-muted-foreground">MMSI</p>
                  <p className="font-medium text-sm lg:text-base truncate">{decrypted.mmsi}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {selectedBoat.note && (
          <div className="mt-4 pt-4 border-t">
            <h3 className="text-xs lg:text-sm font-medium text-muted-foreground mb-2">Note</h3>
            <p className="text-xs lg:text-sm bg-gray-50 p-3 rounded break-words">{selectedBoat.note}</p>
          </div>
        )}
      </div>

      {/* Section maintenance et articles critiques */}
      <div className="space-y-4 lg:space-y-6">
        {/* Coût des maintenances de l'année */}
        <Card className="border-0 shadow-sm bg-gradient-to-r from-nautic/5 to-nautic-gold/10">
          <CardContent className="p-4 lg:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-nautic-gold/20 rounded-lg">
                  <Euro className="h-5 w-5 text-nautic" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground">Coût des maintenances</p>
                    <Select 
                      value={selectedCostYear.toString()} 
                      onValueChange={(value) => setSelectedCostYear(parseInt(value))}
                    >
                      <SelectTrigger className="w-24 h-7 text-sm bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableYears.map(year => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-2xl font-bold text-nautic">
                    {coutMaintenancesAnnee.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {nombreMaintenancesAnnee} maintenance{nombreMaintenancesAnnee > 1 ? 's' : ''} avec coût renseigné
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/maintenance')} 
                variant="outline" 
                size="sm"
                className="hidden lg:flex"
              >
                Voir détails
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Maintenances à venir */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-medium flex items-center gap-2">
              <Bot className="h-5 w-5 text-nautic-gold" />
              <span>Maintenances à venir</span>
              {maintenancesEnRetard.length > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {maintenancesEnRetard.length} en retard
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {prochainesMaintenances.length > 0 ? (
              <div className="space-y-3">
                {prochainesMaintenances.map(maintenance => {
                  const IconComponent = categoryIcons[maintenance.categorie];
                  const isOverdue = maintenance.date_echeance < today;
                  const isDueSoon = !isOverdue && new Date(maintenance.date_echeance).getTime() - new Date().getTime() <= 7 * 24 * 60 * 60 * 1000; // 7 jours
                  
                  // Définir les couleurs selon l'urgence
                  let bgColor = 'bg-gray-50';
                  let textColor = 'text-muted-foreground';
                  let badgeClass = getStatusColor(maintenance.statut);
                  
                  if (isOverdue) {
                    bgColor = 'bg-red-50 border border-red-200';
                    textColor = 'text-red-600';
                    badgeClass = 'bg-red-100 text-red-800';
                  } else if (isDueSoon) {
                    bgColor = 'bg-orange-50 border border-orange-200';
                    textColor = 'text-orange-600';
                    badgeClass = 'bg-orange-100 text-orange-800';
                  } else if (maintenance.statut === 'En attente') {
                    bgColor = 'bg-green-50 border border-green-200';
                  }
                  
                  return (
                    <div key={maintenance.id} className={`p-3 rounded-lg ${bgColor}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">{maintenance.titre}</h4>
                          <p className={`text-xs flex items-center gap-1 mt-1 ${textColor}`}>
                            <Calendar className="h-3 w-3" />
                            {format(new Date(maintenance.date_echeance), "dd/MM/yyyy", { locale: fr })}
                            {isOverdue && <span className="font-semibold">• EN RETARD</span>}
                            {isDueSoon && !isOverdue && <span className="font-semibold">• BIENTÔT</span>}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge className={badgeClass}>
                            {maintenance.statut}
                          </Badge>
                          <div className="flex items-center gap-1">
                            {IconComponent && <IconComponent className="h-3 w-3" />}
                            <span className="text-xs text-muted-foreground">{maintenance.categorie}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <Button 
                  onClick={() => navigate('/maintenance')} 
                  variant="outline" 
                  className="w-full mt-2"
                >
                  Voir toutes les maintenances
                </Button>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">Aucune maintenance programmée</p>
                <Button 
                  onClick={() => navigate('/maintenance')} 
                  className="mt-2 bg-nautic-gold hover:bg-nautic-lightgold text-nautic"
                >
                  Programmer une maintenance
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Articles critiques */}
        <ArticlesCritiquesCard selectedBateauId={selectedBoat.id} />
      </div>
    </div>
  );
};

export default DashboardPage;
